/**
 * Provenance = business events on an item (a signing, a grading, an ownership transfer), stored in
 * dedicated relational tables. `ProvenanceType` is a tenant's event definition; `Provenance` is an
 * actual recorded event. Mirrors backend/src/schemas/Provenance.ts.
 */

export type ProvenanceWorkflow =
  | 'registration'
  | 'identification'
  | 'verification'
  | 'protection'
  | 'manual';

export const PROVENANCE_WORKFLOWS: ProvenanceWorkflow[] = [
  'registration',
  'identification',
  'verification',
  'protection',
  'manual',
];

export const PROVENANCE_WORKFLOW_LABELS: Record<ProvenanceWorkflow, string> = {
  registration: 'Registration',
  identification: 'Identification',
  verification: 'Verification',
  protection: 'Protection',
  manual: 'Manual',
};

export type ProvenanceFieldType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'location'
  | 'select'
  // Picks another item; its id is stored as the field value. On job completion the fingerprinted
  // item is linked to the picked one.
  | 'item';

export const PROVENANCE_FIELD_TYPES: ProvenanceFieldType[] = [
  'string',
  'number',
  'boolean',
  'date',
  'location',
  'select',
  'item',
];

/**
 * A `location` field value. Matches the shape the mobile app stores in `provenance.data`, so both
 * clients write the same object: `country` is an ISO-3166 alpha-2 code (e.g. "CZ"). Legacy/manual
 * values may still be a plain string; readers handle both.
 */
export interface ProvenanceLocation {
  city?: string;
  state?: string;
  country?: string;
}

export interface ProvenanceField {
  key: string;
  label: string;
  type: ProvenanceFieldType;
  required?: boolean;
  unit?: string;
  /** Fixed choices for a `select` field (e.g. Grading Type, Sale Channel). */
  options?: string[];
  /** Auto-fill on form open: date -> today, location -> current location. */
  autoFill?: boolean;
  /** When set, the value is also written onto the item at publicMetadata.details[itemFieldKey]. */
  itemFieldKey?: string;
}

export interface ProvenanceType {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  workflows: ProvenanceWorkflow[];
  fields: ProvenanceField[];
  createdAt: string;
  updatedAt: string;
}

/**
 * An Origin Identity Flow — a named capture flow in the Origin Identity nav. Built-in locked flows
 * are Origin Fingerprinting (fingerprinting) and Origin Verification (verification); admins add
 * custom flows. Each flow runs ONE operation and records events per operation stage (no picker):
 * an OF (fingerprinting) flow does fingerprinting AND verification, so it records both its
 * `fingerprinting` and `verification` events; an OV (verification) flow records `verification`
 * events. Stored on `tenant.publicMetadata.originFlows`.
 */
export type OriginFlowOperation = 'fingerprinting' | 'verification';

/** Event type ids recorded per operation stage. `fingerprinting` applies only to OF flows. */
export interface OriginFlowEvents {
  fingerprinting?: string[];
  verification?: string[];
}

export type OriginFlowClassificationMode = 'auto' | 'fixedSku';

/** Per-flow classification config (see the backend originFlowSchema for the contract). */
export interface OriginFlowClassification {
  /** `auto` (default) runs image classification; `fixedSku` skips it and pins `fixedSkuId`. */
  mode?: OriginFlowClassificationMode;
  /** L2 distance cutoff override (0..2), auto mode. Overrides the tenant default / built-in 0.9. */
  threshold?: number;
  /** Pinned SKU id, fixedSku mode: classification is skipped and every capture uses this SKU. */
  fixedSkuId?: string;
}

export interface OriginIdentityFlow {
  /** Stable id — built-ins use fixed ids; custom flows a uuid (route /origin/flow/:id). */
  id: string;
  name: string;
  /** Icon key (see flow-icons). Falls back to an operation-based default. */
  icon?: string;
  /** The two built-in Origin flows: events + icon are editable, but name/delete are not. */
  locked?: boolean;
  operation: OriginFlowOperation;
  events: OriginFlowEvents;
  /**
   * Replace an identified item's images with this flow's captures. For a flow that records a
   * physical change to the card (a signing), so the item shows what it looks like now. Never
   * touches the SKU's images or the fingerprint — the identity stays bound to the original capture.
   */
  replaceItemImages?: boolean;
  /** Classification behavior (threshold override / fixed SKU). Undefined = auto, 0.9 default. */
  classification?: OriginFlowClassification;
}

/** Fixed ids for the two built-in locked flows. */
const ORIGIN_FINGERPRINTING_FLOW_ID = 'origin-fingerprinting';
const ORIGIN_VERIFICATION_FLOW_ID = 'origin-verification';

const asIds = (v: any): string[] | undefined => (Array.isArray(v) ? v : undefined);

/** Clean a stored classification blob to the known shape; undefined when nothing is set. */
const normalizeClassification = (raw: any): OriginFlowClassification | undefined => {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  // `auto` is implicit, so only `fixedSku` is stored as an explicit mode. fixedSkuId is meaningful
  // only in fixed mode; threshold only in auto mode.
  const mode: OriginFlowClassificationMode | undefined =
    raw.mode === 'fixedSku' ? 'fixedSku' : undefined;
  const threshold =
    mode !== 'fixedSku' && typeof raw.threshold === 'number' ? raw.threshold : undefined;
  const hasFixedSkuId = mode === 'fixedSku' && typeof raw.fixedSkuId === 'string' && !!raw.fixedSkuId;
  const fixedSkuId = hasFixedSkuId ? raw.fixedSkuId : undefined;
  if (!mode && threshold === undefined) {
    return undefined;
  }
  return {
    ...(mode ? { mode } : {}),
    ...(threshold !== undefined ? { threshold } : {}),
    ...(fixedSkuId ? { fixedSkuId } : {}),
  };
};

/** Normalize a stored flow — tenant metadata is free-form json, so the shape isn't guaranteed. */
const normalizeFlow = (raw: any): OriginIdentityFlow => ({
  id: String(raw?.id ?? ''),
  name: String(raw?.name ?? ''),
  icon: raw?.icon,
  locked: !!raw?.locked,
  operation: raw?.operation === 'fingerprinting' ? 'fingerprinting' : 'verification',
  replaceItemImages: !!raw?.replaceItemImages,
  classification: normalizeClassification(raw?.classification),
  events: {
    fingerprinting: asIds(raw?.events?.fingerprinting),
    verification: asIds(raw?.events?.verification),
  },
});

/** All stored Origin Identity flows (custom + the two locked built-ins once saved). */
const getOriginFlows = (publicMetadata: any): OriginIdentityFlow[] => {
  const flows = publicMetadata?.originFlows;
  return Array.isArray(flows) ? flows.map(normalizeFlow) : [];
};

/** Trim a flow to its stored shape (icon only for custom flows; drop empty event slots). */
export const serializeOriginFlow = (f: OriginIdentityFlow): OriginIdentityFlow => {
  const classification = normalizeClassification(f.classification);
  return {
    id: f.id,
    name: f.name.trim(),
    operation: f.operation,
    ...(!f.locked && f.icon ? { icon: f.icon } : {}),
    ...(f.locked ? { locked: true } : {}),
    ...(f.replaceItemImages ? { replaceItemImages: true } : {}),
    ...(classification ? { classification } : {}),
    events: {
      ...(f.events.fingerprinting?.length ? { fingerprinting: f.events.fingerprinting } : {}),
      ...(f.events.verification?.length ? { verification: f.events.verification } : {}),
    },
  };
};

/** Flattened event type ids a flow fires: OF = fingerprinting + verification; OV = verification. */
export const flowEventTypeIds = (flow: OriginIdentityFlow): string[] =>
  flow.operation === 'fingerprinting'
    ? [...(flow.events.fingerprinting ?? []), ...(flow.events.verification ?? [])]
    : flow.events.verification ?? [];

/** The two built-in locked flows, in their default (event-less) form. */
const LOCKED_ORIGIN_FLOWS: OriginIdentityFlow[] = [
  {
    id: ORIGIN_FINGERPRINTING_FLOW_ID,
    name: 'Origin Fingerprinting',
    locked: true,
    operation: 'fingerprinting',
    events: {},
  },
  {
    id: ORIGIN_VERIFICATION_FLOW_ID,
    name: 'Origin Verification',
    locked: true,
    operation: 'verification',
    events: {},
  },
];

/**
 * All Origin Identity flows for a tenant: the two locked built-ins (merged with any stored icon /
 * events overrides) followed by custom flows. The canonical order shown in the nav + manager.
 */
export const getAllOriginFlows = (publicMetadata: any): OriginIdentityFlow[] => {
  const stored = getOriginFlows(publicMetadata);
  const byId = new Map(stored.map((f) => [f.id, f]));
  const locked = LOCKED_ORIGIN_FLOWS.map((d) => {
    const s = byId.get(d.id);
    // No `icon`: the built-ins keep their static icons — serializeOriginFlow never stores one for a
    // locked flow and the editor hides the picker for them, so reading it back would always be
    // undefined.
    if (!s) {
      return d;
    }
    return {
      ...d,
      events: s.events,
      replaceItemImages: s.replaceItemImages,
      classification: s.classification,
    };
  });
  const builtinIds: string[] = [ORIGIN_FINGERPRINTING_FLOW_ID, ORIGIN_VERIFICATION_FLOW_ID];
  const custom = stored.filter((f) => !f.locked && !builtinIds.includes(f.id));
  return [...locked, ...custom];
};

export interface Provenance {
  id: string;
  tenantId: string;
  itemId: string;
  jobId?: string | null;
  provenanceTypeId?: string | null;
  name: string;
  channel: ProvenanceWorkflow;
  occurredAt: string;
  data: Record<string, any>;
  createdBy?: { id?: string; email?: string; name?: string } | null;
  /** Item ref, present only on the SKU-level (cross-item) list; feeds the SN/FN/UID meta line. */
  item?: {
    id: string;
    title: string;
    uniqueId: string;
    externalId?: string | null;
    publicMetadata?: Record<string, any>;
  } | null;
  createdAt: string;
  updatedAt: string;
}
